import { useState, useEffect, useCallback } from "react";
import { Trash2, MailOpen, CheckCheck, ChevronDown } from "lucide-react";
import { adminAxios } from "../../lib/admin";

const fmt = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const ESTADOS = {
  nueva: "NUEVA",
  leida: "LEÍDA",
  atendida: "ATENDIDA",
};

export default function AdminInbox({ onNewMessages }) {
  const [messages, setMessages] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    adminAxios
      .get("/admin/messages")
      .then(({ data }) => {
        setMessages(data.messages);
        onNewMessages(data.messages.filter((m) => m.estado === "nueva").length);
      })
      .catch(() => setError("No se pudo cargar el buzón."));
  }, [onNewMessages]);

  useEffect(() => {
    load();
  }, [load]);

  const setEstado = async (id, estado) => {
    try {
      await adminAxios.patch(`/admin/messages/${id}`, { estado });
    } catch {
      setError("No se pudo actualizar el mensaje.");
    }
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje definitivamente?")) return;
    try {
      await adminAxios.delete(`/admin/messages/${id}`);
    } catch {
      setError("No se pudo eliminar el mensaje.");
    }
    load();
  };

  const openMsg = (m) => {
    setOpenId(openId === m.id ? null : m.id);
    if (m.estado === "nueva") setEstado(m.id, "leida");
  };

  if (error) {
    return (
      <p data-testid="admin-inbox-error" role="alert" className="text-sm text-onix-silver border border-dashed border-onix-silver p-5">
        {error}
      </p>
    );
  }
  if (!messages) return <p className="tech-label">CARGANDO BUZÓN…</p>;

  return (
    <div data-testid="admin-inbox" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif font-bold text-2xl text-onix-white">Buzón de solicitudes</h2>
        <p className="tech-label">{messages.length} MENSAJES</p>
      </div>

      {messages.length === 0 && (
        <p data-testid="admin-inbox-empty" className="text-sm text-onix-gray border border-onix-line p-6">
          Todavía no hay solicitudes en el buzón.
        </p>
      )}

      <ul className="space-y-3">
        {messages.map((m) => (
          <li key={m.id} data-testid={`admin-message-${m.id}`} className="border border-onix-line bg-onix-black2">
            <button
              data-testid={`admin-message-toggle-${m.id}`}
              className="w-full flex flex-wrap items-center gap-3 p-5 text-left"
              onClick={() => openMsg(m)}
              aria-expanded={openId === m.id}
            >
              <span
                className={`text-[10px] font-bold tracking-[0.12em] px-2.5 py-1 border ${
                  m.estado === "nueva" ? "bg-onix-white text-onix-black border-onix-white" : "text-onix-silver border-onix-line"
                }`}
              >
                {ESTADOS[m.estado] || m.estado}
              </span>
              <span className="font-bold text-sm text-onix-white">{m.nombre}</span>
              <span className="text-xs text-onix-gray">· {m.empresa} · {m.cargo}</span>
              <span className="text-xs text-onix-silver ml-auto">{m.area}</span>
              <span className="tech-label">{fmt(m.created_at)}</span>
              <ChevronDown size={14} className={`text-onix-gray transition-transform ${openId === m.id ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {openId === m.id && (
              <div data-testid={`admin-message-body-${m.id}`} className="border-t border-onix-line p-5 space-y-4">
                <div className="grid md:grid-cols-3 gap-3 text-xs">
                  <p className="text-onix-silver">
                    <span className="tech-label block mb-1">EMAIL</span>
                    <a href={`mailto:${m.email}`} className="text-onix-white underline underline-offset-2">{m.email}</a>
                  </p>
                  <p className="text-onix-silver">
                    <span className="tech-label block mb-1">TELÉFONO</span>
                    {m.telefono || "—"}
                  </p>
                  <p className="text-onix-silver">
                    <span className="tech-label block mb-1">ÁREA DE INTERÉS</span>
                    {m.area}
                  </p>
                </div>
                <div>
                  <span className="tech-label block mb-2">MENSAJE</span>
                  <p className="text-sm leading-relaxed text-onix-silver whitespace-pre-wrap border border-onix-line p-4">{m.mensaje}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    data-testid={`admin-mark-leida-${m.id}`}
                    className="btn-onix-secondary !px-4 !py-2 inline-flex items-center gap-2"
                    onClick={() => setEstado(m.id, "leida")}
                  >
                    <MailOpen size={13} aria-hidden="true" /> Marcar leída
                  </button>
                  <button
                    data-testid={`admin-mark-atendida-${m.id}`}
                    className="btn-onix-secondary !px-4 !py-2 inline-flex items-center gap-2"
                    onClick={() => setEstado(m.id, "atendida")}
                  >
                    <CheckCheck size={13} aria-hidden="true" /> Marcar atendida
                  </button>
                  <button
                    data-testid={`admin-delete-${m.id}`}
                    className="btn-onix-secondary !px-4 !py-2 inline-flex items-center gap-2 ml-auto"
                    onClick={() => remove(m.id)}
                  >
                    <Trash2 size={13} aria-hidden="true" /> Eliminar
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
